import { createAsyncThunk } from '@reduxjs/toolkit'
import { patientHelper } from '../../patient/services/patient-service';
import {VobPostBody, FacilityPayers, vobClassificationsItem} from '../models/vob';
import { admissionsAdvisorUtils } from "../utils/admission-advisor-utils";
import { admissionsAdvisorService } from '../services/admissions-advisor-services';
import {Estimate, Id} from "@finpay-estimation/estimation-types";
import {showStatus} from "../../security/state/user-slice";
import { Vob  } from '@finpay-estimation/estimation-types';
import { vobSlice } from '../state/vob-slice';
import { findAndSetRateAsPayorPlan } from '../../estimates/utils/payor-plan-utils';

// handles add and edit cases.
export const saveVob = createAsyncThunk( 
  'vobContext/saveVob',
  async (vob: VobPostBody, thunkAPI) => {
    let response;
    if (vob?.vobBody.vobId) { // edit
      response = await admissionsAdvisorService.updateVob(vob?.vobBody.vobId, vob)
    } else { // add
      response = await admissionsAdvisorService.newVob(vob)
    }
    if (response.hasErrors) {
      patientHelper.robustErrorHandler(response, thunkAPI);
    }
    return response.entity;
  }
)

export const getVob = createAsyncThunk(
  "vobContext/getVob",
  async (config: any, thunkAPI) => {
    const response = await admissionsAdvisorService.getVob(config.paramId, config.vobId);
    if (response.hasErrors) {
      patientHelper.robustErrorHandler(response, thunkAPI);
    } else {
      return response.entity;
    }
  }
);

export const getVobV3 = createAsyncThunk(
  "vobContext/getVobV3",
  async (vobId: number, thunkAPI) => {
    const response = await admissionsAdvisorService.getVobV3(vobId);
    if (response.hasErrors) {
      patientHelper.robustErrorHandler(response, thunkAPI);
    } else {
      // After loading the VOB, try to find and set the rateAsPayorPlan
      const vob = response.entity;
      if (vob?.facility?.facilityId) {
        try {
          const facilityPayersResponse = await admissionsAdvisorService.getEstimatorFacilityLocPayers(-2, vob.facility.facilityId);
          if (!facilityPayersResponse.hasErrors && facilityPayersResponse.entity && facilityPayersResponse.entity.facilityPayors) {
            const facilityPayers = facilityPayersResponse.entity.facilityPayors;
          }
        } catch (error) {
          console.error('Error setting rateAsPayorPlan:', error);
          // Don't fail the VOB load if we can't set the rateAsPayorPlan
        }
      }
      
      return response.entity;
    }
  }
);

export const getVobClassifications = createAsyncThunk(
  "vobContext/getVobClassifications",
  async (paramId: number, thunkAPI) => {
    const response = await admissionsAdvisorService.getVobClassifications(paramId);
    if (response.hasErrors) {
      patientHelper.robustErrorHandler(response, thunkAPI);
    } else {
      return response.entity;
    }
  }
);

export const getEstimatorFacilityPayers = createAsyncThunk(
  "vobContext/getEstimatorFacilityPayers",
  async (params: any, thunkAPI) => {
    const response = await admissionsAdvisorService.getEstimatorFacilityLocPayers(params.paramId, params.facilityId);
    if (response.hasErrors) {
      patientHelper.robustErrorHandler(response, thunkAPI);
    } else {
      let responseBody = {...response.entity};
      responseBody.masterListPayers = params.masterListPayers;
      const cleanedData:FacilityPayers[] = admissionsAdvisorUtils.fillClientPayerDataGaps(responseBody);
      return cleanedData;
    }
  }
);

export const getVOBForPatientRecords = createAsyncThunk(
    'vobContext/getVOBForPatientRecords',
    async (vobId: Id, thunkAPI) => {
        const response = await admissionsAdvisorService.getVob(0, vobId as number);
        if (response?.hasErrors) {
            patientHelper.robustErrorHandler(response, thunkAPI);
        }
        return response.entity;
    }
);

export const saveVOBForFinpassPatientRecords = createAsyncThunk(
    'vobContext/saveVOBForFinpassPatientRecords',
    async (params: {vob: VobPostBody}, thunkAPI) => {
        const {vob} = params;

        const vobId = vob.vobBody.vobId as number;

        const isUpdate = !!vob?.vobBody.vobId;

        const vobResponse = await admissionsAdvisorService.updateVob(vobId, vob);

        if (vobResponse?.hasErrors) {
            patientHelper.robustErrorHandler(vobResponse, thunkAPI);
        } else {
            thunkAPI.dispatch(showStatus(isUpdate ? 'VOB Updated' : 'VOB Created'));
        }

        return vobResponse.entity;
    }
);

export const postVob = createAsyncThunk(
    'vobContext/postVob',
    async (vob: VobPostBody, thunkAPI) => {
        const response = await admissionsAdvisorService.newVob(vob)

        if (response.hasErrors) {
            patientHelper.robustErrorHandler(response, thunkAPI);
        }
        return response.entity;
    }
);

export const populateVOBFromAvailityIntegration = async (
    patientId: number,
    clientId: number,
    facilityId: number,
    estimateId: number,
    vobClassificationsState:vobClassificationsItem[],
    payorId: number,
    isInNetwork: boolean,
    payorPlanId: number
): Promise<any> => {
    try {
        if(estimateId===null || isNaN(estimateId)){
            return null;
        }
        const responseFromAvaility = await admissionsAdvisorService.getVOBFromAvailityIntegration(
            patientId,
            facilityId
        );

        const estimateResponse = await admissionsAdvisorService.callNewEstimateGet(-2, estimateId);

        if(estimateResponse.entity.facilityLevelOfCare !==null && estimateResponse.entity.facilityLevelOfCare.length!==0){
            type VobClassification = {
                vobClassificationId: number;
            };

            type FacilityLevel = {
                vobClassificationId: number;
            };
            const facilityLevels: FacilityLevel[] = estimateResponse.entity.facilityLevelOfCare || [];
            const inNetwClassifications: VobClassification[] = responseFromAvaility.entity.inNetwVobClassifications || [];
            const ooNetwClassifications: VobClassification[] = responseFromAvaility.entity.ooNetwVobClassifications || [];

            const allVobClassificationIds = new Set<number>([
                ...inNetwClassifications.map((item: VobClassification) => item.vobClassificationId),
                ...ooNetwClassifications.map((item: VobClassification) => item.vobClassificationId),
            ]);

            const unmatchedClassifications = facilityLevels.filter(
                (level: FacilityLevel) => !allVobClassificationIds.has(level.vobClassificationId)
            );

            if(unmatchedClassifications.length!==0){
                for (const item of unmatchedClassifications) {
                    const missingVovClassification: vobClassificationsItem =  vobClassificationsState[item.vobClassificationId-1];
                    if(isInNetwork){
                        responseFromAvaility.entity.inNetwVobClassifications.push({
                            coInsurance: null,
                            coPay: null,
                            copayType: null,
                            maxDays: null,
                            vobClassificationId: missingVovClassification.vobClassificationId
                        });
                    }else{
                        responseFromAvaility.entity.ooNetwVobClassifications.push({
                            coInsurance: null,
                            coPay: null,
                            copayType: null,
                            maxDays: null,
                            vobClassificationId: missingVovClassification.vobClassificationId
                        });
                    }
                }
            }

        }

        const newVob = {
            advisorPatientId: patientId,
            fpClientId: clientId,
            fpClientFacilityId: facilityId,
            vobBody: {
                ...responseFromAvaility.entity,
                client: {
                    clientId: clientId
                },
                facility: {
                    facilityId: facilityId
                },
                payer: {
                    payorId: payorId
                },
                plan: {
                    payorPlanId: payorPlanId
                }
            }
        };


        const responseNewVob =  await admissionsAdvisorService.newVob(newVob);
        const updateEstimate: Estimate = {
            ...estimateResponse.entity,
            vobId: responseNewVob.entity.vobId,
        };
        const updateEstimateVobIdResponse = await admissionsAdvisorService.callNewEstimatePut(updateEstimate, estimateId);
        console.log('updateEstimateVobIdResponse', updateEstimateVobIdResponse);


        return responseNewVob.entity;
    } catch (error) {
        console.error('Failed to get VOB from Availity Integration:', error);
        throw error;
    }
};

export const downloadVobPdf = createAsyncThunk(
    'vobContext/downloadVobPdf',
    async (vobId: number, thunkAPI) => {
        try {
            const response = await admissionsAdvisorService.downloadVobPdf(vobId);
            if (response.hasErrors) {
                patientHelper.robustErrorHandler(response, thunkAPI);
                thunkAPI.dispatch(showStatus('Failed to download VOB PDF'));
                return null;
            }
            thunkAPI.dispatch(showStatus('VOB PDF downloaded successfully'));
            return response.entity;
        } catch (error) {
            thunkAPI.dispatch(showStatus('Failed to download VOB PDF'));
            return null;
        }
    }
);

